// The four practice sheets: Loop, Speed, Cues, Test me. Each applies at once; there is no Apply button.

import type { CueMode, LoopPhrases } from '../engine/engine.js';
import { engine } from '../engine/engine.js';
import { CUE_NAMES, formatTime } from './actions.js';
import { Choice, Option, Switch } from './controls.js';
import { useEngineState } from './engine-state.js';
import { Sheet } from './sheet.js';

const SIZES: readonly LoopPhrases[] = [1, 2, 4, 8];

/** Move the loop one 8-count earlier or later: seek into that phrase and loop it (the loop snaps to the 1 of an 8-count). */
function moveLoop(direction: -1 | 1): void {
  const { loop } = engine.getState();
  const starts = engine.phraseStarts();
  let i = starts.findIndex((t) => t >= loop.start - 1e-3);
  if (i < 0) i = 0;
  const target = starts[Math.min(starts.length - 1, Math.max(0, i + direction))];
  if (target === undefined) return;
  engine.seek(target + 1e-3);
  engine.setLoop(true);
}

export function LoopSheet() {
  const s = useEngineState();
  const { loop } = s;
  return (
    <Sheet title="Loop">
      <Switch
        label="Loop this 8-count"
        hint={loop.on ? `From ${formatTime(loop.start)} to ${formatTime(loop.end)}` : 'Repeats from the 1 of the count you are in.'}
        checked={loop.on}
        onChange={(on) => engine.setLoop(on)}
      />
      <div class="field">
        <div class="field-label" id="loop-size">How many 8-counts</div>
        <Choice<LoopPhrases>
          label="How many 8-counts in the loop"
          value={loop.phrases}
          options={SIZES.map((n) => ({ value: n, label: `×${n}`, name: n === 1 ? '×1, one 8-count' : `×${n}, ${n} 8-counts` }))}
          onChange={(n) => { engine.setLoopSize(n); if (!loop.on) engine.setLoop(true); }}
        />
      </div>
      {loop.on && (
        <div class="field">
          <div class="field-label">Move the loop</div>
          <div class="pair">
            <button type="button" class="btn" onClick={() => moveLoop(-1)}>Earlier</button>
            <button type="button" class="btn" onClick={() => moveLoop(1)}>Later</button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

export function SpeedSheet() {
  const s = useEngineState();
  const pct = Math.round(s.speed * 100);
  const set = (p: number) => engine.setSpeed(Math.min(100, Math.max(50, p)) / 100);
  return (
    <Sheet title="Speed">
      <p class="readout" aria-hidden="true">{pct}%</p>
      <div class="slider-row">
        <button type="button" class="btn icon" aria-label="5% slower" disabled={pct <= 50} onClick={() => set(pct - 5)}>−</button>
        <input
          type="range"
          min={50}
          max={100}
          step={5}
          value={pct}
          aria-label="Speed"
          aria-valuetext={`${pct} percent`}
          onInput={(e) => set(Number(e.currentTarget.value))}
        />
        <button type="button" class="btn icon" aria-label="5% faster" disabled={pct >= 100} onClick={() => set(pct + 5)}>+</button>
      </div>
      <p class="note">The pitch stays where it is. The count follows the music.</p>
    </Sheet>
  );
}

const CUE_HINTS: Record<CueMode, string> = {
  off: 'Silent.',
  steps: '1 2 3 · 5 6 7',
  oneFive: 'Only the 1 and the 5.',
  all: 'All eight counts.',
};

export function CuesSheet() {
  const s = useEngineState();
  return (
    <Sheet title="Cues">
      <div class="options" role="radiogroup" aria-label="Which counts make a sound">
        {(['off', 'steps', 'oneFive', 'all'] as const).map((m) => (
          <Option key={m} group="cues" label={CUE_NAMES[m]} hint={CUE_HINTS[m]} checked={s.cueMode === m} onChange={() => engine.setCueMode(m)} />
        ))}
      </div>
      <div class="field">
        <label class="field-label" for="cue-volume">Volume</label>
        <input id="cue-volume" type="range" min={0} max={100} value={Math.round(s.cueVolume * 100)} aria-valuetext={`${Math.round(s.cueVolume * 100)} percent`}
          onInput={(e) => engine.setCueVolume(Number(e.currentTarget.value) / 100)} />
      </div>
      <p class="note">The 1 is the highest sound, the 5 the next, the rest a soft tick.</p>
    </Sheet>
  );
}

export function TestMeSheet() {
  const s = useEngineState();
  return (
    <Sheet title="Test me">
      <p class="note">The count hides for a few 8-counts, then comes back, so you can check you held it.</p>
      <div class="options" role="radiogroup" aria-label="How long the count hides">
        <Option group="testme" label="Off" checked={s.testMe === null} onChange={() => engine.testMe(null)} />
        {([2, 4, 8] as const).map((n) => (
          <Option key={n} group="testme" label={`Hide ${n} 8-counts`} hint={`Shown for ${n}, hidden for ${n}, and so on.`} checked={s.testMe === n} onChange={() => engine.testMe(n)} />
        ))}
      </div>
    </Sheet>
  );
}
